import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { OverheadCategory } from "@/hooks/use-overhead-categories";
import {
  useDeactivateOverheadCategory,
  useReactivateOverheadCategory,
} from "@/hooks/use-overhead-categories";
import { DeactivateOverheadEntityDialog } from "./DeactivateOverheadEntityDialog";

interface OverheadCategoryRowProps {
  category: OverheadCategory;
  isAdmin: boolean;
}

export function OverheadCategoryRow({ category, isAdmin }: OverheadCategoryRowProps) {
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const deactivate = useDeactivateOverheadCategory();
  const reactivate = useReactivateOverheadCategory();

  return (
    <>
      <tr className="border-b border-border hover:bg-muted/30 transition-colors">
        <td className="px-4 py-3">
          <Link
            to="/dashboard/overhead-categories/$categoryId"
            params={{ categoryId: category.id }}
            className="group inline-flex items-center gap-1 font-mono text-sm font-semibold text-foreground hover:text-primary"
          >
            <span className="rounded px-1.5 py-0.5 bg-muted group-hover:bg-primary/10 transition-colors">
              {category.code}
            </span>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
          </Link>
        </td>
        <td className="px-4 py-3 text-sm">{category.label}</td>
        <td className="px-4 py-3 text-sm text-muted-foreground">
          {category.activeSubcategoryCount}/{category.totalSubcategoryCount} active
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              category.active ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"
            }`}
          >
            {category.active ? "Active" : "Inactive"}
          </span>
        </td>
        {isAdmin && (
          <td className="px-4 py-3 text-right">
            <div className="flex items-center justify-end gap-1">
              {category.active ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setDeactivateOpen(true)}
                  title="Deactivate"
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                  disabled={reactivate.isPending}
                  onClick={() => reactivate.mutate(category.id)}
                  title="Reactivate"
                >
                  Reactivate
                </Button>
              )}
            </div>
          </td>
        )}
      </tr>

      {deactivateOpen && (
        <DeactivateOverheadEntityDialog
          open
          onOpenChange={(open) => !open && setDeactivateOpen(false)}
          entityType="category"
          entityId={category.id}
          entityName={`${category.code} — ${category.label}`}
          isPending={deactivate.isPending}
          onConfirm={() =>
            deactivate.mutate(category.id, { onSuccess: () => setDeactivateOpen(false) })
          }
        />
      )}
    </>
  );
}
